import { isAcCable, isAcTerminalId } from "./ac";
import type { SimulateContext } from "./cable-current";
import type { DefLookup } from "./catalog";
import { dcPortTerminalGroups, pvInputTerminalGroups } from "./component-ports";
import { computeEnergySnapshot } from "./diagnostics/energy";
import { batteryCurrentsFromEnergyFlow } from "./diagnostics/connected-runtime";
import { buildGraph, computeActiveSolarPaths, computeActiveSourceCircuits, computeEnergizedTerminals, isDcSideSupplied, reachable, v } from "./diagnostics/graph";
import { getEffectiveStateOption, applyStateToAmp } from "./simulate/states";
import { SCHEMA_VERSION } from "./schema/version";
import type { Cable, System, SystemNode } from "./types";

const EMPTY_LOADS = {};
const INFER_ENERGIZED = {};
export type LiveCircuitFlow = { byCable: Map<string, number>; byBattery: Map<string, number> };
type StateCache = WeakMap<object, WeakMap<object, WeakMap<object, LiveCircuitFlow>>>;
const cache = new WeakMap<Record<string, SystemNode>, WeakMap<Cable[], WeakMap<DefLookup, StateCache>>>();

/** Actual DC conductor currents from the same dispatch and battery allocation
 * as the energy/battery cards. Static design sizing never calls this solver.
 * Copper junctions collapse to one vertex; cable loops divide by length/area
 * resistance when every cable in the conductor network specifies its area;
 * otherwise the network uses equal cable resistance. This is a
 * linear routing of a planning ledger, not a battery/charger voltage solver. */
export function computeLiveCableCurrents(nodesById: Record<string, SystemNode>, cables: Cable[], lookup: DefLookup, simulate: SimulateContext): Map<string, number> {
  return computeLiveCircuitFlow(nodesById, cables, lookup, simulate).byCable;
}

export function computeLiveCircuitFlow(nodesById: Record<string, SystemNode>, cables: Cable[], lookup: DefLookup, simulate: SimulateContext): LiveCircuitFlow {
  let byCables = cache.get(nodesById); if (!byCables) { byCables = new WeakMap(); cache.set(nodesById, byCables); }
  let byLookup = byCables.get(cables); if (!byLookup) { byLookup = new WeakMap(); byCables.set(cables, byLookup); }
  let byStates = byLookup.get(lookup); if (!byStates) { byStates = new WeakMap(); byLookup.set(lookup, byStates); }
  let byLoads = byStates.get(simulate.componentStates); if (!byLoads) { byLoads = new WeakMap(); byStates.set(simulate.componentStates, byLoads); }
  const loads = simulate.loadStates ?? EMPTY_LOADS;
  let byEnergized = byLoads.get(loads); if (!byEnergized) { byEnergized = new WeakMap(); byLoads.set(loads, byEnergized); }
  const energizedKey = simulate.energizedTerminals ?? INFER_ENERGIZED;
  const known = byEnergized.get(energizedKey); if (known) return known;
  const system: System = { name: "Live circuit", version: SCHEMA_VERSION, zoneDistances: {}, nodes: Object.values(nodesById), cables };
  const energized = simulate.energizedTerminals ?? computeEnergizedTerminals(system, lookup, simulate);
  const { flow } = computeEnergySnapshot(system, lookup, simulate.componentStates, {}, loads, energized);
  const injections = new Map(flow.terminalCurrents);
  const add = (vertex: string, current: number) => injections.set(vertex, (injections.get(vertex) ?? 0) + current);
  // A modeled Vbatt+ lead is a leaf outside the conducting graph. Transfer
  // its signed current to its peer so upstream wiring sees the same draw.
  for (const cable of cables) {
    const amps = flow.monitorSupplyCableCurrents.get(cable.id);
    if (!amps) continue;
    add(v(cable.fromNodeId, cable.fromTerminalId), -amps);
    add(v(cable.toNodeId, cable.toTerminalId), amps);
  }
  const byBattery = batteryCurrentsFromEnergyFlow(system, lookup, flow);
  for (const [id, current] of byBattery) {
    const node = nodesById[id]; if (node?.kind !== "component") continue;
    const def = lookup(node.definitionId, node.customDefinition); if (!def) continue;
    const pos = def.terminals.filter(t => t.polarity === "positive");
    const neg = def.terminals.filter(t => t.polarity === "negative");
    for (const t of pos) add(v(id, t.id), -current / pos.length);
    for (const t of neg) add(v(id, t.id), current / neg.length);
  }
  const graph = buildGraph(system, lookup, { simulate, excludeSense: true });
  // PV has an operating-point current model (Imp × sun state), distinct
  // from the derated watts delivered on the controller's battery side. Seed
  // every panel in a complete directed string: series midpoint injections
  // cancel and parallel string heads add. The controller consumes that same
  // PV current on its input pair, including a physically common negative.
  const solarPaths = computeActiveSolarPaths(system, lookup, simulate);
  const activePanels = new Set(solarPaths.flatMap(path => [...path.panelNodeIds]));
  const panelPositive = new Map<string, number>();
  for (const id of activePanels) {
    const node = nodesById[id]; if (node?.kind !== "component") continue;
    const def = lookup(node.definitionId, node.customDefinition); if (!def) continue;
    const amps = applyStateToAmp(def.ampOutput, getEffectiveStateOption(def, id, simulate), "output") ?? 0;
    const pos = def.terminals.filter(t => t.polarity === "positive"), neg = def.terminals.filter(t => t.polarity === "negative");
    for (const t of pos) { const vertex = v(id, t.id); add(vertex, amps / pos.length); panelPositive.set(vertex, amps / pos.length); }
    for (const t of neg) add(v(id, t.id), -amps / neg.length);
  }
  const pvPorts = solarPaths.flatMap(path => {
    const node = nodesById[path.controllerNodeId]; if (node?.kind !== "component") return [];
    const def = lookup(node.definitionId, node.customDefinition); if (!def) return [];
    return pvInputTerminalGroups(def).flatMap(port => {
      const pos = port.filter(t => t.polarity === "positive"), neg = port.filter(t => t.polarity === "negative");
      if (!pos.length || !neg.length) return [];
      const reach = new Set(pos.flatMap(t => [...reachable(graph, v(node.id, t.id))]));
      return [{ id: node.id, pos, neg, reach }];
    });
  });
  for (const port of pvPorts) {
    let amps = 0;
    for (const [vertex, current] of panelPositive) if (port.reach.has(vertex)) {
      const destinations = pvPorts.filter(other => other.reach.has(vertex)).length;
      amps += current / destinations;
    }
    for (const t of port.pos) add(v(port.id, t.id), -amps / port.pos.length);
    for (const t of port.neg) add(v(port.id, t.id), amps / port.neg.length);
  }
  const parent = new Map<string, string>();
  const root = (x: string): string => { const p = parent.get(x); if (p == null) { parent.set(x, x); return x; } if (p === x) return x; const r = root(p); parent.set(x, r); return r; };
  const union = (a: string, b: string) => { const ra = root(a), rb = root(b); if (ra !== rb) parent.set(ra, rb); };
  for (const [a, edges] of graph.adj) for (const { vertex: b, via } of edges) {
    root(a); root(b); if (via.kind === "intra") union(a, b);
  }
  const omitted = new Set<string>();
  let starterSources: ReturnType<typeof computeActiveSourceCircuits> | undefined;
  for (const node of system.nodes) {
    if (node.kind !== "component") continue;
    const def = lookup(node.definitionId, node.customDefinition); if (!def) continue;
    // Starter cranking is deliberately not an instantaneous energy load. Its
    // state/surge calculation remains with the cranking model.
    if (def.category === "engine" && (applyStateToAmp(def.ampDraw, getEffectiveStateOption(def, node.id, simulate), "draw") ?? 0) > 0) {
      // Only a complete, active starter loop needs the separate cranking
      // model. Omitting an open loop would restore unsigned endpoint current
      // on its remaining cable, including a shared return carrying other loads.
      starterSources ??= computeActiveSourceCircuits(system, lookup, simulate);
      if (!isDcSideSupplied(node, def, "input", starterSources)) continue;
      for (const port of dcPortTerminalGroups(def, "input")) for (const t of port) omitted.add(root(v(node.id, t.id)));
    }
  }
  type Edge = { id: string; a: string; b: string; g: number; specified: boolean };
  const edges: Edge[] = [];
  const adjacency = new Map<string, Edge[]>();
  for (const cable of cables) {
    if (isAcCable(cable) || isAcTerminalId(cable.fromTerminalId) || isAcTerminalId(cable.toTerminalId)) continue;
    const va = v(cable.fromNodeId, cable.fromTerminalId), vb = v(cable.toNodeId, cable.toTerminalId);
    if (!graph.adj.get(va)?.some(e => e.via.kind === "cable" && e.via.cableId === cable.id)) continue;
    const a = root(va), b = root(vb);
    const specified = cable.wireSizeMm2Override != null && cable.wireSizeMm2Override > 0;
    const resistance = specified ? Math.max(cable.lengthM, .001) / cable.wireSizeMm2Override! : 1;
    const edge = { id: cable.id, a, b, g: 1 / resistance, specified }; edges.push(edge);
    (adjacency.get(a) ?? adjacency.set(a, []).get(a)!).push(edge);
    (adjacency.get(b) ?? adjacency.set(b, []).get(b)!).push(edge);
  }
  const current = new Map<string, number>();
  for (const [vertex, amps] of injections) addAtRoot(root(vertex), amps);
  function addAtRoot(vertex: string, amps: number) { current.set(vertex, (current.get(vertex) ?? 0) + amps); }
  const result = new Map(flow.monitorSupplyCableCurrents);
  const visited = new Set<string>();
  for (const seed of adjacency.keys()) {
    if (visited.has(seed)) continue;
    const vertices = [seed]; visited.add(seed);
    for (let i = 0; i < vertices.length; i++) for (const e of adjacency.get(vertices[i]) ?? []) {
      const next = e.a === vertices[i] ? e.b : e.a;
      if (!visited.has(next)) { visited.add(next); vertices.push(next); }
    }
    if (vertices.some(vertex => omitted.has(vertex))) continue;
    const net = vertices.reduce((sum, vertex) => sum + (current.get(vertex) ?? 0), 0);
    // An incomplete or unsupported port must never invent a balancing source.
    if (Math.abs(net) > 1e-7) continue;
    const set = new Set(vertices), groupEdges = edges.filter(e => set.has(e.a));
    // Never compare an arbitrary unknown resistance against a physical L/A.
    if (groupEdges.some(e => !e.specified)) for (const e of groupEdges) e.g = 1;
    if (vertices.every(vertex => Math.abs(current.get(vertex) ?? 0) < 1e-10)) {
      for (const e of groupEdges) result.set(e.id, 0);
      continue;
    }
    // Ground the last algebraic vertex. Gaussian elimination of the reduced
    // Laplacian gives unique cable currents on connected resistive networks.
    const index = new Map(vertices.map((vertex, i) => [vertex, i]));
    const n = vertices.length - 1;
    const matrix = Array.from({ length: n }, (_, i) => [...Array<number>(n).fill(0), current.get(vertices[i]) ?? 0]);
    for (const e of groupEdges) {
      if (e.a === e.b) continue;
      const a = index.get(e.a)!, b = index.get(e.b)!;
      if (a < n) matrix[a][a] += e.g;
      if (b < n) matrix[b][b] += e.g;
      if (a < n && b < n) { matrix[a][b] -= e.g; matrix[b][a] -= e.g; }
    }
    let solved = true;
    for (let col = 0; col < n; col++) {
      let pivot = col;
      for (let row = col + 1; row < n; row++) if (Math.abs(matrix[row][col]) > Math.abs(matrix[pivot][col])) pivot = row;
      if (Math.abs(matrix[pivot][col]) < 1e-12) { solved = false; break; }
      [matrix[col], matrix[pivot]] = [matrix[pivot], matrix[col]];
      const scale = matrix[col][col];
      for (let j = col; j <= n; j++) matrix[col][j] /= scale;
      for (let row = 0; row < n; row++) if (row !== col) {
        const factor = matrix[row][col];
        for (let j = col; j <= n; j++) matrix[row][j] -= factor * matrix[col][j];
      }
    }
    if (!solved) continue;
    const potential = (vertex: string) => { const i = index.get(vertex)!; return i < n ? matrix[i][n] : 0; };
    for (const e of groupEdges) { const amps = e.g * (potential(e.a) - potential(e.b)); result.set(e.id, Math.abs(amps) < 1e-9 ? 0 : Math.round(amps * 1e12) / 1e12); }
  }
  const solved = { byCable: result, byBattery };
  byEnergized.set(energizedKey, solved);
  return solved;
}
